import { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import styles from './ProgressRing.module.scss';

export interface ProgressRingProps {
  value: number;
  max?: number;
  size?: number;
  thickness?: number;
  color?: string;
  showValue?: boolean;
  label?: string;
  animate?: boolean;
}

/**
 * Reusable D3 Progress Ring Component
 *
 * Circular progress indicator with smooth animations.
 * Ideal for displaying percentages and completion rates.
 *
 * @example
 * <ProgressRing
 *   value={85}
 *   max={100}
 *   size={200}
 *   color="var(--success)"
 *   showValue
 *   label="Success Rate"
 * />
 */
export function ProgressRing({
  value,
  max = 100,
  size = 120,
  thickness = 12,
  color = 'var(--primary)',
  showValue = true,
  label,
  animate = true,
}: ProgressRingProps) {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || size === 0) {
      return;
    }

    // Clear previous ring
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Calculate dimensions
    const radius = size / 2 - thickness / 2;
    const circumference = 2 * Math.PI * radius;
    const percentage = Math.min(Math.max(value / max, 0), 1);
    const offset = circumference * (1 - percentage);

    // Create chart group (centered)
    const chart = svg
      .append('g')
      .attr('transform', `translate(${size / 2},${size / 2})`);

    // Background ring
    chart
      .append('circle')
      .attr('class', styles.progressRing__background)
      .attr('r', radius)
      .attr('fill', 'none')
      .attr('stroke', 'var(--border)')
      .attr('stroke-width', thickness)
      .attr('opacity', 0.2);

    // Progress ring
    const progressRing = chart
      .append('circle')
      .attr('class', styles.progressRing__progress)
      .attr('r', radius)
      .attr('fill', 'none')
      .attr('stroke', color)
      .attr('stroke-width', thickness)
      .attr('stroke-linecap', 'round')
      .attr('stroke-dasharray', circumference)
      .attr('transform', 'rotate(-90)');

    if (animate) {
      progressRing
        .attr('stroke-dashoffset', circumference)
        .transition()
        .duration(1000)
        .ease(d3.easeCubicOut)
        .attr('stroke-dashoffset', offset);
    } else {
      progressRing.attr('stroke-dashoffset', offset);
    }

    // Center content
    if (showValue) {
      const centerGroup = svg
        .append('g')
        .attr('transform', `translate(${size / 2},${size / 2})`);

      // Value text
      centerGroup
        .append('text')
        .attr('class', styles.progressRing__value)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .attr('dy', label ? '-0.5em' : '0')
        .text(`${Math.round((value / max) * 100)}%`);

      // Label text
      if (label) {
        centerGroup
          .append('text')
          .attr('class', styles.progressRing__label)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'central')
          .attr('dy', '1.2em')
          .text(label);
      }
    }
  }, [value, max, size, thickness, color, showValue, label, animate]);

  if (size === 0) {
    return null;
  }

  return (
    <svg
      ref={svgRef}
      width={size}
      height={size}
      className={styles.progressRing}
      role="img"
      aria-label={`Progress: ${Math.round((value / max) * 100)}%`}
    />
  );
}
